import { useState, useEffect } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import { useTranslation } from "react-i18next";
import ErrorBanner from "../components/ErrorBanner";

// ─── RESPONSIVE HOOK ─────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

export default function NewReport({ token, wells, onBack, onSaved }) {
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  const [form, setForm] = useState({
    wellId: "",
    date: new Date().toISOString().split("T")[0],
    operations: "",
    footage: "",
    mud_weight: "",
    bit_depth: "",
    hours_drilling: "",
  });
  const [fieldNotes, setFieldNotes] = useState("");
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const headers = {
    Authorization: "Bearer " + token,
    "Content-Type": "application/json",
  };

  const cyan = "#00f5ff";
  const border = "rgba(0,245,255,0.15)";
  const card = "rgba(0,245,255,0.03)";

  const inputStyle = {
    width: "100%",
    backgroundColor: "rgba(0,245,255,0.05)",
    border: "1px solid rgba(0,245,255,0.2)",
    borderRadius: "6px",
    padding: "10px 14px",
    color: "white",
    fontSize: "13px",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "'Segoe UI', sans-serif",
  };

  const labelStyle = {
    color: cyan,
    fontSize: "10px",
    letterSpacing: "2px",
    textTransform: "uppercase",
    display: "block",
    marginBottom: "6px",
  };

  const sectionStyle = {
    backgroundColor: card,
    border: `1px solid ${border}`,
    borderRadius: "10px",
    padding: isMobile ? "16px" : "20px",
    marginBottom: "16px",
  };

  const sectionLabel = {
    color: cyan,
    fontSize: "10px",
    fontWeight: "700",
    letterSpacing: "3px",
    marginBottom: "16px",
  };

  function updateForm(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleGenerate() {
    if (!form.wellId) {
      setError(t("newReport.selectWellFirst"));
      return;
    }
    if (!fieldNotes.trim()) {
      setError(t("newReport.addNotesFirst"));
      return;
    }
    setError("");
    setGenerating(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/reports/generate`,
        { wellId: form.wellId, notes: fieldNotes, date: form.date },
        { headers },
      );
      setGenerated(res.data.generated);
      setForm((f) => ({ ...f, operations: res.data.generated }));
    } catch (err) {
      setError(
        err.response?.data?.error ||
          t("newReport.aiFailed"),
      );
    } finally {
      setGenerating(false);
    }
  }

  async function handleSubmit() {
    if (!form.wellId || !form.operations) {
      setError(t("newReport.required"));
      return;
    }
    setSaving(true);
    setError("");
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/reports`,
        {
          ...form,
          footage: Number(form.footage) || 0,
          mud_weight: Number(form.mud_weight) || 0,
          bit_depth: Number(form.bit_depth) || 0,
          hours_drilling: Number(form.hours_drilling) || 0,
        },
        { headers },
      );
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || t("newReport.saveFailed"));
    } finally {
      setSaving(false);
    }
  }

  function handleExportPDF() {
    const well = wells.find((w) => (w.id || w._id) === form.wellId);
    const wellName = well ? well.well_name : "Unknown Well";
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 48;
    const colW = pageW - margin * 2;
    let y = margin;

    doc.setFillColor(2, 8, 23);
    doc.rect(0, 0, pageW, 70, "F");
    doc.setFontSize(22);
    doc.setTextColor(0, 245, 255);
    doc.setFont("helvetica", "bold");
    doc.text("RIGIQ", margin, 36);
    doc.setFontSize(9);
    doc.setTextColor(100, 180, 200);
    doc.setFont("helvetica", "normal");
    doc.text("FIELD OPTIMIZATION PLATFORM", margin, 52);
    doc.setFontSize(11);
    doc.setTextColor(0, 245, 255);
    doc.setFont("helvetica", "bold");
    doc.text("DAILY OPERATIONS REPORT", pageW - margin, 36, { align: "right" });
    doc.setFontSize(9);
    doc.setTextColor(100, 180, 200);
    doc.setFont("helvetica", "normal");
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageW - margin, 52, {
      align: "right",
    });

    y = 90;
    doc.setDrawColor(0, 245, 255);
    doc.setLineWidth(0.5);
    doc.line(margin, y, pageW - margin, y);
    y += 16;

    doc.setFontSize(9);
    doc.setTextColor(0, 245, 255);
    doc.setFont("helvetica", "bold");
    doc.text("WELL", margin, y);
    doc.text("DATE", margin + colW / 3, y);
    doc.text("REPORT ID", margin + (colW / 3) * 2, y);
    y += 14;
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.text(wellName, margin, y);
    doc.text(form.date, margin + colW / 3, y);
    doc.text(
      `RPT-${Date.now().toString().slice(-6)}`,
      margin + (colW / 3) * 2,
      y,
    );
    y += 24;

    doc.setDrawColor(40, 60, 80);
    doc.setLineWidth(0.3);
    doc.line(margin, y, pageW - margin, y);
    y += 16;

    doc.setFontSize(9);
    doc.setTextColor(0, 245, 255);
    doc.setFont("helvetica", "bold");
    doc.text("DRILLING DATA", margin, y);
    y += 14;

    const drillingFields = [
      ["Footage Drilled", form.footage ? `${form.footage} ft` : "—"],
      ["Mud Weight", form.mud_weight ? `${form.mud_weight} ppg` : "—"],
      ["Bit Depth", form.bit_depth ? `${form.bit_depth} ft` : "—"],
      [
        "Hours Drilling",
        form.hours_drilling ? `${form.hours_drilling} hrs` : "—",
      ],
    ];
    drillingFields.forEach(([label, value], i) => {
      const x = margin + (i % 2) * (colW / 2);
      if (i % 2 === 0 && i > 0) y += 28;
      doc.setFontSize(8);
      doc.setTextColor(120, 160, 180);
      doc.setFont("helvetica", "normal");
      doc.text(label.toUpperCase(), x, y);
      doc.setFontSize(12);
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.text(value, x, y + 14);
    });
    y += 42;

    doc.setDrawColor(40, 60, 80);
    doc.setLineWidth(0.3);
    doc.line(margin, y, pageW - margin, y);
    y += 16;

    if (fieldNotes.trim()) {
      doc.setFontSize(9);
      doc.setTextColor(0, 245, 255);
      doc.setFont("helvetica", "bold");
      doc.text("FIELD NOTES", margin, y);
      y += 14;
      doc.setFontSize(10);
      doc.setTextColor(200, 200, 200);
      doc.setFont("helvetica", "normal");
      const noteLines = doc.splitTextToSize(fieldNotes, colW);
      noteLines.forEach((line) => {
        if (y > 720) {
          doc.addPage();
          y = margin;
        }
        doc.text(line, margin, y);
        y += 14;
      });
      y += 10;
      doc.setDrawColor(40, 60, 80);
      doc.setLineWidth(0.3);
      doc.line(margin, y, pageW - margin, y);
      y += 16;
    }

    if (form.operations.trim()) {
      doc.setFontSize(9);
      doc.setTextColor(0, 245, 255);
      doc.setFont("helvetica", "bold");
      doc.text("OPERATIONS NARRATIVE", margin, y);
      if (generated) {
        doc.setFontSize(8);
        doc.setTextColor(0, 180, 80);
        doc.text("AI GENERATED", margin + 140, y);
      }
      y += 14;
      doc.setFontSize(10);
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "normal");
      const opLines = doc.splitTextToSize(form.operations, colW);
      opLines.forEach((line) => {
        if (y > 720) {
          doc.addPage();
          y = margin;
        }
        doc.text(line, margin, y);
        y += 14;
      });
    }

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setDrawColor(0, 245, 255);
      doc.setLineWidth(0.3);
      doc.line(margin, 760, pageW - margin, 760);
      doc.setFontSize(8);
      doc.setTextColor(80, 120, 140);
      doc.setFont("helvetica", "normal");
      doc.text("RIGIQ Field Optimization Platform — Confidential", margin, 774);
      doc.text(`Page ${i} of ${pageCount}`, pageW - margin, 774, {
        align: "right",
      });
    }

    doc.save(`RIGIQ_Report_${wellName.replace(/\s+/g, "_")}_${form.date}.pdf`);
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#020817",
        fontFamily: "'Segoe UI', sans-serif",
      }}
    >
      {/* NAV */}
      <nav
        style={{
          backgroundColor: "rgba(0,0,0,0.6)",
          borderBottom: `1px solid ${border}`,
          padding: isMobile ? "12px 16px" : "14px 28px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backdropFilter: "blur(10px)",
        }}
      >
        <div
          style={{
            fontSize: isMobile ? "18px" : "22px",
            fontWeight: "900",
            letterSpacing: "4px",
            background: "linear-gradient(135deg, #00f5ff, #0080ff)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          RIGIQ
        </div>
        <button
          onClick={onBack}
          style={{
            color: "rgba(0,245,255,0.5)",
            background: "none",
            border: `1px solid ${border}`,
            cursor: "pointer",
            fontSize: "11px",
            padding: "5px 12px",
            borderRadius: "4px",
            letterSpacing: "1px",
          }}
        >
          {t("newReport.back")}
        </button>
      </nav>

      <main
        style={{
          padding: isMobile ? "16px" : "28px",
          maxWidth: "760px",
          margin: "0 auto",
        }}
      >
        <h2
          style={{
            fontSize: isMobile ? "18px" : "22px",
            fontWeight: "800",
            letterSpacing: "2px",
            color: "white",
            marginBottom: "4px",
          }}
        >
          {t("newReport.title")}
        </h2>
        <p
          style={{
            color: "rgba(0,245,255,0.4)",
            fontSize: "11px",
            letterSpacing: "3px",
            marginBottom: "24px",
          }}
        >
          {t("newReport.subtitle")}
        </p>

        {/* SECTION 1 — WELL + DATE */}
        <div style={sectionStyle}>
          <div style={sectionLabel}>{t("newReport.section1")}</div>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "16px" }}
          >
            <div>
              <label style={labelStyle}>{t("newReport.well")}</label>
              <select
                value={form.wellId}
                onChange={(e) => updateForm("wellId", e.target.value)}
                style={inputStyle}
              >
                <option value="">{t("newReport.selectWell")}</option>
                {wells.map((w) => (
                  <option key={w.id || w._id} value={w.id || w._id}>
                    {w.well_name}
                  </option>
                ))}
              </select>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "16px",
              }}
            >
              <div>
                <label style={labelStyle}>{t("newReport.reportDate")}</label>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => updateForm("date", e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>{t("newReport.hoursDrilling")}</label>
                <input
                  type="number"
                  value={form.hours_drilling}
                  onChange={(e) => updateForm("hours_drilling", e.target.value)}
                  placeholder="0"
                  style={inputStyle}
                />
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 2 — DRILLING DATA */}
        <div style={sectionStyle}>
          <div style={sectionLabel}>{t("newReport.section2")}</div>
          {/* 3 cols desktop, 1 col mobile */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
              gap: "16px",
            }}
          >
            <div>
              <label style={labelStyle}>{t("newReport.footage")}</label>
              <input
                type="number"
                value={form.footage}
                onChange={(e) => updateForm("footage", e.target.value)}
                placeholder="0"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>{t("newReport.mudWeight")}</label>
              <input
                type="number"
                value={form.mud_weight}
                onChange={(e) => updateForm("mud_weight", e.target.value)}
                placeholder="0.0"
                step="0.1"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>{t("newReport.bitDepth")}</label>
              <input
                type="number"
                value={form.bit_depth}
                onChange={(e) => updateForm("bit_depth", e.target.value)}
                placeholder="0"
                style={inputStyle}
              />
            </div>
          </div>
        </div>

        {/* SECTION 3 — FIELD NOTES + AI */}
        <div style={sectionStyle}>
          <div style={sectionLabel}>{t("newReport.section3")}</div>
          <div
            style={{
              color: "rgba(255,255,255,0.3)",
              fontSize: "11px",
              marginBottom: "16px",
            }}
          >
            {t("newReport.notesHelp")}
          </div>
          <textarea
            value={fieldNotes}
            onChange={(e) => setFieldNotes(e.target.value)}
            rows={isMobile ? 4 : 5}
            placeholder={t("newReport.notesPlaceholder")}
            style={{ ...inputStyle, resize: "vertical", marginBottom: "12px" }}
          />
          <button
            onClick={handleGenerate}
            disabled={generating || !form.wellId || !fieldNotes.trim()}
            style={{
              width: "100%",
              background: generating
                ? "rgba(0,245,255,0.1)"
                : "linear-gradient(135deg, #00f5ff, #0080ff)",
              color: generating ? "rgba(0,245,255,0.5)" : "#020817",
              fontWeight: "700",
              fontSize: isMobile ? "11px" : "12px",
              letterSpacing: isMobile ? "1px" : "3px",
              padding: "12px",
              borderRadius: "6px",
              border: generating ? `1px solid ${border}` : "none",
              cursor: generating ? "not-allowed" : "pointer",
              boxShadow: generating ? "none" : "0 0 20px rgba(0,245,255,0.3)",
              transition: "all 0.2s",
            }}
          >
            {generating ? t("newReport.generating") : t("newReport.generate")}
          </button>
        </div>

        {/* SECTION 4 — OPERATIONS NARRATIVE */}
        <div
          style={{
            ...sectionStyle,
            border: `1px solid ${generated ? "rgba(0,245,255,0.4)" : border}`,
            transition: "border-color 0.3s",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "12px",
              flexWrap: "wrap",
              gap: "6px",
            }}
          >
            <div
              style={{
                color: cyan,
                fontSize: "10px",
                fontWeight: "700",
                letterSpacing: "3px",
              }}
            >
              {t("newReport.section4")}
            </div>
            {generated && (
              <span
                style={{
                  color: "#00cc66",
                  fontSize: "10px",
                  letterSpacing: "2px",
                }}
              >
                {t("newReport.aiGenerated")}
              </span>
            )}
          </div>
          {generated && (
            <div
              style={{
                backgroundColor: "rgba(0,245,255,0.05)",
                border: `1px solid rgba(0,245,255,0.15)`,
                borderRadius: "6px",
                padding: "14px",
                marginBottom: "12px",
                color: "rgba(255,255,255,0.6)",
                fontSize: "12px",
                lineHeight: "1.8",
                fontStyle: "italic",
              }}
            >
              <div
                style={{
                  color: "rgba(0,245,255,0.4)",
                  fontSize: "9px",
                  letterSpacing: "2px",
                  marginBottom: "8px",
                }}
              >
                {t("newReport.aiDraft")}
              </div>
              {generated}
            </div>
          )}
          <textarea
            value={form.operations}
            onChange={(e) => updateForm("operations", e.target.value)}
            rows={isMobile ? 6 : 8}
            placeholder={
              generated
                ? t("newReport.editPlaceholder")
                : t("newReport.opsPlaceholder")
            }
            style={{ ...inputStyle, resize: "vertical", lineHeight: "1.8" }}
          />
        </div>

        {error && <ErrorBanner message={error} />}

        {/* SUBMIT + EXPORT */}
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <button
            onClick={onBack}
            style={{
              flex: 1,
              minWidth: isMobile ? "100%" : "120px",
              background: "none",
              border: `1px solid ${border}`,
              color: "rgba(0,245,255,0.5)",
              fontWeight: "700",
              fontSize: "11px",
              letterSpacing: "2px",
              padding: "12px",
              borderRadius: "6px",
              cursor: "pointer",
            }}
          >
            {t("newReport.cancel")}
          </button>

          {form.operations && form.wellId && (
            <button
              onClick={handleExportPDF}
              style={{
                flex: 1,
                minWidth: isMobile ? "100%" : "120px",
                background: "none",
                border: "1px solid rgba(0,245,255,0.4)",
                color: cyan,
                fontWeight: "700",
                fontSize: "11px",
                letterSpacing: "2px",
                padding: "12px",
                borderRadius: "6px",
                cursor: "pointer",
              }}
            >
              {t("newReport.exportPdf")}
            </button>
          )}

          <button
            onClick={handleSubmit}
            disabled={saving || !form.wellId || !form.operations}
            style={{
              flex: 2,
              minWidth: isMobile ? "100%" : "160px",
              background: saving
                ? "rgba(0,245,255,0.1)"
                : "linear-gradient(135deg, #00f5ff, #0080ff)",
              color: saving ? "rgba(0,245,255,0.5)" : "#020817",
              fontWeight: "700",
              fontSize: "11px",
              letterSpacing: "3px",
              padding: "12px",
              borderRadius: "6px",
              border: "none",
              cursor: saving ? "not-allowed" : "pointer",
              boxShadow: saving ? "none" : "0 0 20px rgba(0,245,255,0.3)",
            }}
          >
            {saving ? t("newReport.saving") : t("newReport.submit")}
          </button>
        </div>
      </main>
    </div>
  );
}
